import { useAuth } from "@contexts/auth";
import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import {
  DoctorProfileActivity,
  DoctorProfileActivityResponse,
} from "@customTypes/dashboard";

import { dashboardQKeys } from "./queryKeys";


const mapFunnel = (funnel: any) => ({
  doctorProfileViewed: funnel?.doctor_profile_viewed ?? 0,
  serviceSelected: funnel?.service_selected ?? 0,
  slotSelected: funnel?.slot_selected ?? 0,
  bookingStarted: funnel?.booking_started ?? 0,
  bookingCompleted: funnel?.booking_completed ?? 0,
  paymentStarted: funnel?.payment_started ?? 0,
  paymentCompleted: funnel?.payment_completed ?? 0,
});

const mapAcquisition = (row: any) => ({
  source: row.source ?? "Direct",
  medium: row.medium ?? "None",
  sessions: row.sessions ?? 0,
  users: row.users ?? 0,
  botSessions: row.bot_sessions ?? 0,
});

const mapReferrer = (row: any) => ({
  domain: row.domain ?? "",
  sessions: row.sessions ?? 0,
});

const mapDoctor = (doctor: any): DoctorProfileActivity => ({
  doctorId: doctor.doctor_id,
  profileId: doctor.profile_id,
  slug: doctor.slug,
  name: doctor.name,
  profileImage: doctor.profile_image,
  specialization: doctor.specialization,
  sessions: doctor.sessions ?? 0,
  users: doctor.users ?? 0,
  botSessions: doctor.bot_sessions ?? 0,
  pagesPerSession: doctor.pages_per_session ?? 0,
  engagementTime: doctor.engagement_time ?? 0,
  scrollDepth: doctor.scroll_depth ?? 0,
  rageClicks: doctor.rage_clicks ?? 0,
  deadClicks: doctor.dead_clicks ?? 0,
  errorClicks: doctor.error_clicks ?? 0,
  scriptErrors: doctor.script_errors ?? 0,
  quickbacks: doctor.quickbacks ?? 0,
  excessiveScrolls: doctor.excessive_scrolls ?? 0,
  urls: doctor.urls ?? [],
  acquisition: (doctor.acquisition ?? []).map(mapAcquisition),
  referrers: (doctor.referrers ?? []).map(mapReferrer),
  funnel: mapFunnel(doctor.funnel),
});

const mapFunnelDoctor = (doctor: any) => ({
  doctorId: doctor.doctor_id,
  profileId: doctor.profile_id,
  slug: doctor.slug,
  name: doctor.name,
  profileImage: doctor.profile_image,
  specialization: doctor.specialization,
  funnel: mapFunnel(doctor.funnel),
});

const select = (response: any): DoctorProfileActivityResponse => {
  const data = response.data;
  return {
    configured: data.configured,
    hasData: data.has_data,
    numDays: data.num_days,
    fetchedAt: data.fetched_at,
    periodStartsAt: data.period_starts_at,
    periodEndsAt: data.period_ends_at,
    coverage: {
      status: data.coverage?.status ?? "unknown",
      percentage: data.coverage?.percentage ?? undefined,
      trackingStartedAt: data.coverage?.tracking_started_at ?? undefined,
    },
    funnelCoverage: {
      status: data.funnel_coverage?.status ?? "unknown",
      percentage: data.funnel_coverage?.percentage ?? undefined,
      trackingStartedAt:
        data.funnel_coverage?.tracking_started_at ?? undefined,
    },
    doctors: (data.doctors ?? []).map(mapDoctor),
    totals: {
      sessions: data.totals?.sessions ?? 0,
      users: data.totals?.users ?? 0,
      botSessions: data.totals?.bot_sessions ?? 0,
      rageClicks: data.totals?.rage_clicks ?? 0,
      deadClicks: data.totals?.dead_clicks ?? 0,
      errorClicks: data.totals?.error_clicks ?? 0,
      scriptErrors: data.totals?.script_errors ?? 0,
      quickbacks: data.totals?.quickbacks ?? 0,
      excessiveScrolls: data.totals?.excessive_scrolls ?? 0,
    },
    funnelTotals: mapFunnel(data.funnel_totals),
    funnelDoctors: (data.funnel_doctors ?? []).map(mapFunnelDoctor),
    acquisitionTotals: (data.acquisition_totals ?? []).map(mapAcquisition),
    topReferrers: (data.top_referrers ?? []).map(mapReferrer),
    metricNames: data.metric_names ?? [],
    unmatchedUrls: data.unmatched_urls ?? [],
  };
};

export type DoctorFunnelPlatform =
  | "website"
  | "patient_mobile"
  | "whatsapp"
  | "kiko"
  | "other"
  | "all";

export const useGetDoctorProfileActivity = (
  numDays: number,
  platform: DoctorFunnelPlatform = "website"
) => {
  const { id, permissions } = useAuth();
  return useQuery({
    queryKey: [...dashboardQKeys.doctorProfileActivity(numDays, platform), id, permissions["reports.view"]],
    queryFn: () =>
      api.get("analytics/doctor-profiles/clarity", {
        params: { num_days: numDays, platform },
      }),
    select,
    staleTime: 1000 * 60,
    refetchOnMount: "always",
  });
};
